import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { Link2, Link2Off, Copy, Check } from "lucide-react";

export default function AdminIntegrations() {
  const [form, setForm] = useState({ clientId: "", apiKey: "", apiUrl: "" });
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const { data: status, refetch } = useQuery<any>({
    queryKey: ["/api/integrations/finance"],
    queryFn: () => apiRequest("GET", "/api/integrations/finance"),
  });

  const handleSave = async () => {
    if (!form.clientId || !form.apiKey || !form.apiUrl) {
      showError("All fields are required", "Missing fields");
      return;
    }
    setSaving(true);
    try {
      await apiRequest("POST", "/api/integrations/finance", form);
      showSuccess("Finance integration connected successfully", "Connected!");
      setForm({ clientId: "", apiKey: "", apiUrl: "" });
      refetch();
    } catch (e: any) {
      showError(e.message || "Failed to connect", "Error");
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm("Disconnect Finance integration? Payments will no longer sync.")) return;
    setDisconnecting(true);
    try {
      await apiRequest("DELETE", "/api/integrations/finance");
      showSuccess("Finance integration disconnected", "Disconnected");
      refetch();
    } catch (e: any) {
      showError(e.message || "Failed to disconnect", "Error");
    } finally {
      setDisconnecting(false);
    }
  };

  const copy = (value: string, key: string) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const isConnected = status?.connected;

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Integrations</h1>
        <p className="text-muted-foreground text-sm mt-1">Connect ChefAiQ to external platforms</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xs">FIN</div>
              <div>
                <CardTitle className="text-base">Finance Platform</CardTitle>
                <CardDescription className="text-xs">Sync subscription payments and procurement expenses</CardDescription>
              </div>
            </div>
            <Badge variant={isConnected ? "default" : "secondary"} className="text-xs">
              {isConnected ? "● Connected" : "○ Not connected"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isConnected ? (
            <div className="space-y-3">
              <div className="bg-muted rounded-lg p-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Client ID</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{status.clientId}</span>
                    <button onClick={() => copy(status.clientId, "clientId")} className="text-muted-foreground hover:text-foreground">
                      {copied === "clientId" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">API URL</span>
                  <span className="font-mono text-xs">{status.apiUrl}</span>
                </div>
                {status.connectedAt && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Connected</span>
                    <span className="text-xs">{new Date(status.connectedAt).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
              <div className="text-xs text-muted-foreground bg-green-50 border border-green-200 rounded p-3">
                ✅ Subscription payments and procurement expenses will automatically sync to Finance.
              </div>
              <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={disconnecting} className="text-destructive border-destructive hover:bg-destructive hover:text-white">
                <Link2Off className="h-4 w-4 mr-2" />
                {disconnecting ? "Disconnecting..." : "Disconnect"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Paste the credentials generated from Finance → Settings → Integrations → ChefAiQ.
              </p>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="apiUrl" className="text-xs">API URL</Label>
                  <Input id="apiUrl" placeholder="https://finance.example.com" value={form.apiUrl}
                    onChange={e => setForm(f => ({ ...f, apiUrl: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="clientId" className="text-xs">Client ID</Label>
                  <Input id="clientId" placeholder="kmc-abc123..." value={form.clientId}
                    onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="apiKey" className="text-xs">API Key</Label>
                  <Input id="apiKey" type="password" placeholder="Paste API key" value={form.apiKey}
                    onChange={e => setForm(f => ({ ...f, apiKey: e.target.value }))} />
                </div>
              </div>
              <Button onClick={handleSave} disabled={saving || !form.clientId || !form.apiKey || !form.apiUrl}>
                <Link2 className="h-4 w-4 mr-2" />
                {saving ? "Connecting..." : "Connect Finance"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
